import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Save, TrendingUp, TrendingDown, AlertTriangle, Award, Info, Wand2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SCENARIOS = [
  { key: 'pacto',        label: 'Pacto / Quita',          accent: 'sky',     help: 'Acuerdo con el deudor. Ingreso = importe pactado.' },
  { key: 'cobertura',    label: 'Subasta / Cobertura',    accent: 'indigo',  help: 'Subasta perdida, se cobra la deuda recuperada. Escenario base del negocio financiero.' },
  { key: 'cdr',          label: 'Subasta / CDR',          accent: 'amber',   help: 'Cesión de remate. Ingreso por defecto = (valor salida + deuda total) / 2.' },
  { key: 'adjudicacion', label: 'Subasta / Adjudicación', accent: 'emerald', help: 'Adjudicación del activo y venta posterior. Ingreso = precio de venta.' },
];

const COST_FIELDS = [
  { key: 'precio_compra_credito', label: 'Precio compra crédito' },
  { key: 'comision',              label: 'Comisión' },
  { key: 'gastos_legales',        label: 'Gastos legales' },
  { key: 'impuestos',             label: 'Impuestos' },
  { key: 'capex',                 label: 'CAPEX / Reforma' },
  { key: 'incentivos',            label: 'Incentivos' },
  { key: 'otros',                 label: 'Otros costes' },
];

const BLANK_ESC = COST_FIELDS.reduce((o, f) => ({ ...o, [f.key]: 0 }), { ingreso: 0, meses: '' });

const BLANK_STATE = {
  escenarios: SCENARIOS.reduce((o, s) => ({ ...o, [s.key]: { ...BLANK_ESC } }), {}),
};

// Legacy fallback kept for offline use; the backend is the source of truth for defaults.
const computeDefaultsFromAsset = (asset) => {
  if (!asset) return null;
  const num = (v) => (typeof v === 'number' && isFinite(v) ? v : 0);
  const precioCompra    = num(asset.precio_compra);
  const valorReferencia = num(asset.valor_referencia);
  const valorSalida     = num(asset.valor_salida_base) || num(asset.valor_salida_base);
  const deudaTotal      = num(asset.deuda_total) || num(asset.deuda_reclamada);
  const valorAdj        = num(asset.valor_adjudicacion);

  const otros_notaria = Math.round(precioCompra * 0.015);
  const capex_reforma = Math.round(precioCompra * 0.15);
  const itp_pacto = Math.round(Math.max(precioCompra, valorReferencia) * 0.10);
  const ajd_compra = Math.round(precioCompra * 0.015);
  const itp_adj = Math.round(Math.max(valorAdj, valorReferencia) * 0.10);

  return {
    escenarios: {
      pacto:        { precio_compra_credito: precioCompra, comision: 0, gastos_legales: 300,  impuestos: itp_pacto,         capex: capex_reforma, incentivos: 0, otros: otros_notaria, ingreso: valorSalida,                           meses: 6 },
      cobertura:    { precio_compra_credito: precioCompra, comision: 0, gastos_legales: 1500, impuestos: ajd_compra,        capex: 0,             incentivos: 0, otros: otros_notaria, ingreso: Math.round(deudaTotal * 1.10),         meses: 10 },
      cdr:          { precio_compra_credito: precioCompra, comision: 0, gastos_legales: 3000, impuestos: ajd_compra,        capex: 0,             incentivos: 0, otros: otros_notaria, ingreso: Math.round((valorSalida + deudaTotal) / 2), meses: 12 },
      adjudicacion: { precio_compra_credito: precioCompra, comision: 0, gastos_legales: 3000, impuestos: ajd_compra + itp_adj, capex: capex_reforma, incentivos: 0, otros: otros_notaria, ingreso: valorSalida,                         meses: 18 },
    },
  };
};

const ACCENT_CLASSES = {
  sky:     { border: 'border-sky-200',     bg: 'bg-sky-50/50',     title: 'text-sky-700',     badge: 'bg-sky-100 text-sky-700' },
  indigo:  { border: 'border-indigo-200',  bg: 'bg-indigo-50/50',  title: 'text-indigo-700',  badge: 'bg-indigo-100 text-indigo-700' },
  amber:   { border: 'border-amber-200',   bg: 'bg-amber-50/50',   title: 'text-amber-700',   badge: 'bg-amber-100 text-amber-700' },
  emerald: { border: 'border-emerald-200', bg: 'bg-emerald-50/50', title: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700' },
};

const cur = (v) => v == null ? '-' : new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
const pct = (v) => v == null ? '-' : `${(v * 100).toFixed(1)}%`;

const scoreColor = (s) => s >= 80 ? 'text-emerald-600' : s >= 60 ? 'text-sky-600' : s >= 40 ? 'text-amber-600' : 'text-red-600';
const scoreBg    = (s) => s >= 80 ? 'bg-emerald-500'  : s >= 60 ? 'bg-sky-500'    : s >= 40 ? 'bg-amber-500'  : 'bg-red-500';

// Scoring Crédito only offers concrete field names. "max_*" codes coming from
// the backend defaults are resolved to whichever concrete field is the winning
// one for this asset, so the user sees the actual base field (e.g. "Deuda
// reclamada" or "Precio de compra") instead of the policy label.
const CONCRETE_BASE_OPTIONS = [
  { value: 'precio_compra',      label: 'Precio de compra' },
  { value: 'valor_referencia',   label: 'Valor de referencia' },
  { value: 'valor_adjudicacion', label: 'Valor de adjudicación' },
  { value: 'deuda_total',        label: 'Deuda reclamada' },
];

const resolveToConcreteSource = (asset, fuente) => {
  if (!asset) return fuente;
  const pc = asset.precio_compra || 0;
  const va = asset.valor_adjudicacion || 0;
  const vr = asset.valor_referencia || 0;
  const dt = asset.deuda_total || asset.deuda_reclamada || 0;
  switch (fuente) {
    case 'max_precio_valref':     return vr >= pc ? 'valor_referencia' : 'precio_compra';
    case 'max_adj_valref':        return vr >= va ? 'valor_referencia' : 'valor_adjudicacion';
    case 'max_adj_valref_precio': {
      const m = Math.max(va, vr, pc);
      if (va === m) return 'valor_adjudicacion';
      if (vr === m) return 'valor_referencia';
      return 'precio_compra';
    }
    case 'max_deuda_precio':      return dt >= pc ? 'deuda_total' : 'precio_compra';
    case 'adj_or_precio':         return va > 0 ? 'valor_adjudicacion' : 'precio_compra';
    default: return fuente;
  }
};

// Resolve a tax base source code against asset data. Mirrors the backend
// `_resolve_tax_base` so the UI can show the base imponible € live.
const resolveBase = (asset, fuente) => {
  if (!asset) return 0;
  const pc = asset.precio_compra || 0;
  const va = asset.valor_adjudicacion || 0;
  const vr = asset.valor_referencia || 0;
  const dt = asset.deuda_total || asset.deuda_reclamada || 0;
  switch (fuente) {
    case 'max_precio_valref':     return Math.max(pc, vr);
    case 'max_adj_valref':        return Math.max(va, vr);
    case 'max_adj_valref_precio': return Math.max(va, vr, pc);
    case 'adj_or_precio':         return va > 0 ? va : pc;
    case 'max_deuda_precio':      return Math.max(dt, pc);
    case 'precio_compra':         return pc;
    case 'valor_referencia':      return vr;
    case 'valor_adjudicacion':    return va;
    case 'deuda_total':           return dt;
    default: return 0;
  }
};

const CreditoScoringTab = ({ asset, assetId, readOnly }) => {
  const [inputs, setInputs] = useState(BLANK_STATE);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  // Tax breakdown per scenario (e.g. [{impuesto:'AJD', tipo:1.5, base:180000, importe:2700}, ...])
  const [taxesDetail, setTaxesDetail] = useState({});
  // Catalog of base-source codes → human-readable labels (from /settings/tax-rules)
  const [baseLabels, setBaseLabels] = useState({});
  // Load labels once
  useEffect(() => {
    axios.get(`${API}/settings/tax-rules`, { withCredentials: true })
      .then(r => setBaseLabels(r.data.base_labels || {}))
      .catch(() => {});
  }, []);

  // Fetch pre-filled defaults from the backend (applies the user-saved settings).
  const fetchDefaultsFromServer = async () => {
    try {
      const r = await axios.get(`${API}/assets/${assetId}/credito-scoring/defaults`, { withCredentials: true });
      if (r.data?.impuestos_detalle) setTaxesDetail(r.data.impuestos_detalle);
      return r.data;
    } catch {
      // Fallback: compute locally with hardcoded defaults (offline safety)
      return computeDefaultsFromAsset(asset);
    }
  };

  // Load saved scoring (if any). If none, auto-fill from backend defaults.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/assets/${assetId}/credito-scoring`, { withCredentials: true });
        if (!alive) return;
        if (r.data?.inputs) {
          setInputs(mergeInputs(r.data.inputs, asset));
          if (r.data?.resultado) setResult(r.data.resultado);
          if (r.data?.updated_at) setUpdatedAt(r.data.updated_at);
          if (r.data?.updated_by_name) setUpdatedBy(r.data.updated_by_name);
        } else {
          const defaults = await fetchDefaultsFromServer();
          if (alive && defaults) setInputs(mergeInputs(defaults, asset));
        }
      } catch {
        const defaults = await fetchDefaultsFromServer();
        if (alive && defaults) setInputs(mergeInputs(defaults, asset));
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [assetId, asset]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyAutofill = async () => {
    const defaults = await fetchDefaultsFromServer();
    if (!defaults) { toast.error('Sin datos de activo para autocompletar'); return; }
    setInputs(mergeInputs(defaults, asset));
    toast.success('Escenarios recalculados con los parámetros actuales');
  };

  // Live preview: recompute on input change (debounced)
  const previewSignature = useMemo(() => JSON.stringify(inputs), [inputs]);
  useEffect(() => {
    if (loading) return;
    const t = setTimeout(async () => {
      try {
        const r = await axios.post(`${API}/assets/${assetId}/credito-scoring/preview`, inputs, { withCredentials: true });
        setResult(r.data);
        if (r.data?.impuestos_detalle) setTaxesDetail(r.data.impuestos_detalle);
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [previewSignature, assetId, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = useCallback((esc, field, value) => {
    setInputs(prev => {
      const nextEsc = { ...prev.escenarios[esc], [field]: value };
      // If the tax config changed, recompute the total live using the asset data
      // so the read-only "Impuestos" field updates immediately (without waiting
      // for the backend preview round-trip).
      if (field === 'impuestos_config' && Array.isArray(value)) {
        const updatedCfg = value.map(row => {
          const base = resolveBase(asset, row.base_fuente);
          const tipo = Number(row.tipo) || 0;
          const importe = Math.round(base * tipo) / 100;
          return { ...row, base, importe };
        });
        const total = updatedCfg.reduce((s, r) => s + (r.importe || 0), 0);
        nextEsc.impuestos_config = updatedCfg;
        nextEsc.impuestos = Math.round(total * 100) / 100;
      }
      return {
        ...prev,
        escenarios: { ...prev.escenarios, [esc]: nextEsc },
      };
    });
  }, [asset]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/assets/${assetId}/credito-scoring`, inputs, { withCredentials: true });
      setResult(r.data.resultado);
      setUpdatedAt(r.data.updated_at);
      toast.success('Scoring crédito guardado');
    } catch (err) {
      toast.error('Error al guardar: ' + (err.response?.data?.detail || err.message));
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  const scenarios = result?.escenarios || {};

  return (
    <div className="space-y-6" data-testid="credito-scoring-tab">
      {/* ===== ASSET DATA CARD — same style as Análisis ROI ===== */}
      <AssetDataCard asset={asset} />

      {/* ===== OVERVIEW HEADER ===== */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <OverviewCard
          label="Score financiero base"
          sublabel="Subasta / Cobertura"
          value={result?.score_financiero_base}
          isScore
          tid="overview-score-base"
        />
        <OverviewCard
          label="Score financiero total"
          sublabel="50% cobertura + 25% peor + 25% media"
          value={result?.score_financiero_total}
          isScore
          highlight
          tid="overview-score-total"
        />
        <OverviewCard
          label="Mejor escenario"
          sublabel={result?.escenario_mejor ? labelFor(result.escenario_mejor) : '-'}
          value={result?.roi_max != null ? pct(result.roi_max) : '-'}
          icon={<TrendingUp className="w-4 h-4 text-emerald-600" />}
          tid="overview-mejor"
        />
        <OverviewCard
          label="Peor escenario"
          sublabel={result?.escenario_peor ? labelFor(result.escenario_peor) : '-'}
          value={result?.roi_min != null ? pct(result.roi_min) : '-'}
          icon={<TrendingDown className="w-4 h-4 text-red-600" />}
          tid="overview-peor"
        />
      </div>

      {updatedAt && (
        <p className="text-xs text-slate-400 flex items-center gap-1">
          <Info className="w-3 h-3" />
          Último guardado: {new Date(updatedAt).toLocaleString('es-ES')}{updatedBy ? ` por ${updatedBy}` : ''}
        </p>
      )}

      {/* Autofill from asset data */}
      {!readOnly && (
        <div className="flex flex-wrap items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg" data-testid="credito-autofill-bar">
          <Wand2 className="w-4 h-4 text-slate-500 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-700">Autocompletar desde datos del activo</p>
            <p className="text-xs text-slate-500 mt-0.5">
              Usa <strong>Precio compra</strong>, <strong>Valor referencia</strong>, <strong>Valor salida</strong>, <strong>Deuda total</strong> y <strong>Valor adjudicación</strong> del activo para aplicar las reglas de negocio por escenario (ITP/AJD, gastos legales, 1.5% notaría, 15% CAPEX, plazos). Tus ediciones manuales se sobrescriben.
            </p>
          </div>
          <button onClick={applyAutofill} className="btn-secondary text-sm flex items-center gap-1.5 whitespace-nowrap" data-testid="credito-autofill-btn">
            <Wand2 className="w-3.5 h-3.5" /> Recalcular desde datos
          </button>
        </div>
      )}

      {/* ===== 4 SCENARIO CARDS ===== */}
      <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-4">
        {SCENARIOS.map(s => (
          <ScenarioCard
            key={s.key}
            scenario={s}
            inputs={inputs.escenarios[s.key] || BLANK_ESC}
            result={scenarios[s.key]}
            taxesDetail={taxesDetail[s.key] || []}
            baseLabels={baseLabels}
            onChange={(field, value) => setField(s.key, field, value)}
            readOnly={readOnly}
            isBest={result?.escenario_mejor === s.key}
            isWorst={result?.escenario_peor === s.key}
          />
        ))}
      </div>

      {/* ===== SAVE BAR ===== */}
      {!readOnly && (
        <div className="flex justify-end pt-2 border-t border-slate-200">
          <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="credito-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Guardando...' : 'Guardar scoring'}
          </button>
        </div>
      )}
    </div>
  );
};

const mergeInputs = (saved, asset) => {
  const out = { ...BLANK_STATE, ...saved };
  out.escenarios = { ...BLANK_STATE.escenarios };
  for (const s of SCENARIOS) {
    const savedEsc = (saved?.escenarios || {})[s.key] || {};
    out.escenarios[s.key] = { ...BLANK_ESC, ...savedEsc };
    // Normalize `impuestos_config`: resolve any "max_*" base codes to the
    // concrete field that wins for this asset so the dropdown shows the actual
    // base name (Precio compra, Valor referencia, etc.) instead of a policy label.
    const rawCfg = Array.isArray(savedEsc.impuestos_config) ? savedEsc.impuestos_config : [];
    out.escenarios[s.key].impuestos_config = rawCfg.map(row => ({
      ...row,
      base_fuente: resolveToConcreteSource(asset, row.base_fuente),
    }));
  }
  return out;
};

const labelFor = (key) => SCENARIOS.find(s => s.key === key)?.label || key;

const OverviewCard = ({ label, sublabel, value, isScore, highlight, icon, tid }) => (
  <div className={`card-base p-4 ${highlight ? 'ring-2 ring-slate-900' : ''}`} data-testid={tid}>
    <div className="flex items-center gap-1.5 text-xs text-slate-500 uppercase tracking-wide mb-1">
      {icon}{label}
    </div>
    <p className={`text-2xl font-bold ${isScore && value != null ? scoreColor(value) : 'text-slate-900'}`}>
      {isScore
        ? (value != null ? value.toFixed(1) : '-')
        : value}
    </p>
    <p className="text-xs text-slate-400 mt-1 truncate" title={sublabel}>{sublabel}</p>
  </div>
);

const ScenarioCard = ({ scenario, inputs, result, taxesDetail, onChange, readOnly, isBest, isWorst, baseLabels }) => {
  const accent = ACCENT_CLASSES[scenario.accent];
  const taxConfig = inputs.impuestos_config || [];
  const setTaxConfig = (newCfg) => onChange('impuestos_config', newCfg);
  return (
    <div className={`card-base p-4 border-2 ${accent.border} ${accent.bg}`} data-testid={`scenario-${scenario.key}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className={`font-bold text-base ${accent.title}`}>{scenario.label}</h3>
          <p className="text-[11px] text-slate-500 leading-tight mt-0.5">{scenario.help}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          {isBest && <span className="text-[10px] font-semibold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded inline-flex items-center gap-1"><Award className="w-3 h-3" /> Mejor</span>}
          {isWorst && <span className="text-[10px] font-semibold bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Peor</span>}
        </div>
      </div>

      {/* Inputs */}
      <div className="space-y-1.5 mb-3">
        {COST_FIELDS.map(f => (
          <React.Fragment key={f.key}>
            {/* Impuestos: readonly total (computed from impuestos_config) */}
            {f.key === 'impuestos' ? (
              <EurInput label={f.label} value={inputs.impuestos} onChange={() => {}} readOnly tid={`${scenario.key}-${f.key}`} />
            ) : (
              <EurInput label={f.label} value={inputs[f.key]} onChange={v => onChange(f.key, v)} readOnly={readOnly} tid={`${scenario.key}-${f.key}`} />
            )}
            {f.key === 'impuestos' && (
              <TaxConfigEditor
                scenarioKey={scenario.key}
                config={taxConfig}
                detail={taxesDetail}
                baseLabels={baseLabels}
                onChange={setTaxConfig}
                readOnly={readOnly}
              />
            )}
          </React.Fragment>
        ))}
        <div className="h-px bg-slate-200 my-2" />
        <EurInput label="Ingreso esperado" value={inputs.ingreso} onChange={v => onChange('ingreso', v)} readOnly={readOnly} tid={`${scenario.key}-ingreso`} strong />
        <div className="flex items-center justify-between gap-2">
          <label className="text-xs text-slate-600 flex-1">Meses estimados</label>
          <input type="number" min="0" step="1" className="input-base text-right text-xs py-1 w-20"
            value={inputs.meses === '' ? '' : inputs.meses}
            onChange={e => onChange('meses', e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
            disabled={readOnly}
            data-testid={`${scenario.key}-meses`} />
        </div>
      </div>

      {/* Outputs */}
      <div className="pt-3 border-t border-slate-300/60 space-y-1">
        <KV label="Coste total"    value={cur(result?.coste_total)} />
        <KV label="Ingreso"        value={cur(result?.ingreso)} />
        <KV label="Margen"         value={cur(result?.margen)} strong
            positive={result && result.margen > 0} negative={result && result.margen < 0} />
        <KV label="ROI"            value={pct(result?.roi)}
            positive={result && result.roi > 0} negative={result && result.roi < 0} />
        <KV label="ROI anualizado" value={result?.roi_anualizado != null ? pct(result.roi_anualizado) : <em className="text-slate-400 not-italic text-[11px]">falta meses</em>}
            positive={result?.roi_anualizado != null && result.roi_anualizado > 0}
            negative={result?.roi_anualizado != null && result.roi_anualizado < 0} />
      </div>

      {/* Score bar */}
      <div className="mt-3 pt-3 border-t border-slate-300/60">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-slate-600">Score escenario</span>
          <span className={`text-lg font-bold ${scoreColor(result?.score ?? 0)}`} data-testid={`${scenario.key}-score`}>
            {result?.score != null ? result.score.toFixed(1) : '-'}
          </span>
        </div>
        <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
          <div className={`h-full rounded-full ${scoreBg(result?.score ?? 0)}`} style={{ width: `${result?.score ?? 0}%` }} />
        </div>
        {result?.datos_incompletos && (
          <p className="text-[10px] text-amber-700 mt-2 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Indica meses para el ROI anualizado
          </p>
        )}
      </div>
    </div>
  );
};

const EurInput = ({ label, value, onChange, readOnly, tid, strong }) => (
  <div className="flex items-center justify-between gap-2">
    <label className={`text-xs flex-1 ${strong ? 'font-semibold text-slate-700' : 'text-slate-600'}`}>{label}</label>
    <div className="relative w-28">
      <input type="number" min="0" step="1"
        className={`input-base text-right text-xs py-1 pr-5 ${strong ? 'font-semibold' : ''}`}
        value={value === 0 && !strong ? '' : value}
        placeholder={strong ? '0' : ''}
        onChange={e => onChange(e.target.value === '' ? 0 : parseFloat(e.target.value) || 0)}
        disabled={readOnly}
        data-testid={tid} />
      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">€</span>
    </div>
  </div>
);

const KV = ({ label, value, strong, positive, negative }) => (
  <div className="flex items-center justify-between">
    <span className="text-xs text-slate-500">{label}</span>
    <span className={`text-xs ${strong ? 'font-bold' : 'font-medium'} ${positive ? 'text-emerald-700' : negative ? 'text-red-600' : 'text-slate-700'}`}>{value}</span>
  </div>
);

export default CreditoScoringTab;

// ======================================================================
// AssetDataCard — read-only card showing the asset fields involved in the
// scoring calculations. Mirrors the "Análisis de Rentabilidad" card style
// used in the ROI tab.
// ======================================================================
const AssetDataCard = ({ asset }) => {
  if (!asset) return null;
  const cells = [
    { label: 'Tipo Activo Operativo', value: asset.tipo_activo_operativo || '—', raw: true },
    { label: `Precio de Compra${asset.precio_compra_es_estimado ? ' *' : ''}`, value: `${cur(asset.precio_compra)}${asset.precio_compra_es_estimado ? ' *' : ''}` },
    { label: 'Valor de Referencia', value: cur(asset.valor_referencia) },
    { label: 'Valor Adjudicación', value: cur(asset.valor_adjudicacion) },
    { label: 'Deuda Total', value: cur(asset.deuda_total || asset.deuda_reclamada) },
    { label: 'Valor Salida', value: cur(asset.valor_salida_base || asset.valor_salida_base) },
    { label: 'Tasación', value: cur(asset.tasacion) },
    { label: 'Vivienda Habitual', value: asset.vivienda_habitual === 'NO' ? 'No (50%)' : asset.vivienda_habitual === 'SI' ? 'Sí (70%)' : 'No consta (70%)', raw: true },
  ];
  return (
    <div className="card-base p-6" data-testid="scoring-asset-data">
      <div className="flex items-center gap-2 mb-4">
        <Info className="w-4 h-4 text-blue-600" />
        <h3 className="heading-3">Datos del activo (usados en el scoring)</h3>
      </div>
      <p className="body-text text-xs text-slate-500 mb-4">Los datos base se editan desde el botón Editar del activo.</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cells.map(c => (
          <div key={c.label}>
            <p className="text-xs text-slate-500 mb-1">{c.label}</p>
            <p className="text-sm font-semibold text-slate-900">{c.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ======================================================================
// TaxConfigEditor — shows 1..N rows of (Impuesto, Base imponible, Tipo %)
// dropdowns under the "Impuestos" field. The resulting amount € is
// displayed read-only. Users can add / remove rows. When any field
// changes we call onChange(newConfig) which updates the scenario's
// `impuestos_config` array — the backend recomputes and returns the
// sum, updating the read-only "Impuestos" input above.
// ======================================================================
const IMPUESTO_OPTIONS = ['ITP', 'AJD', 'IVA'];

const TaxConfigEditor = ({ scenarioKey, config, detail, baseLabels, onChange, readOnly }) => {
  const rows = Array.isArray(config) && config.length > 0 ? config : [];
  const updateRow = (idx, patch) => {
    const next = rows.map((r, i) => i === idx ? { ...r, ...patch } : r);
    onChange(next);
  };
  const addRow = () => {
    onChange([...rows, { impuesto: 'ITP', base_fuente: 'precio_compra', tipo: 10 }]);
  };
  const removeRow = (idx) => {
    onChange(rows.filter((_, i) => i !== idx));
  };

  // Resolve importe for display — prefer the value returned by the backend preview (detail)
  // at the same index; fall back to inline config.importe.
  const importeAt = (idx, row) => {
    if (Array.isArray(detail) && detail[idx] && typeof detail[idx].importe === 'number') return detail[idx].importe;
    return row.importe;
  };
  const baseAt = (idx, row) => {
    if (Array.isArray(detail) && detail[idx] && typeof detail[idx].base === 'number') return detail[idx].base;
    return row.base;
  };

  return (
    <div className="pl-2 pb-1 space-y-2 border-l-2 border-slate-200" data-testid={`${scenarioKey}-tax-editor`}>
      {rows.length === 0 && (
        <p className="text-[10px] text-slate-400 italic">Sin impuestos aplicados en este escenario.</p>
      )}
      {rows.map((row, idx) => (
        <div
          key={idx}
          className="border rounded px-2 py-1.5 space-y-1"
          style={{ background: 'var(--color-bg-subtle)', borderColor: 'var(--color-border)' }}
          data-testid={`${scenarioKey}-tax-row-${idx}`}
        >
          <div className="grid grid-cols-12 gap-1 items-center">
            <select
              className="input-base text-[11px] py-0.5 col-span-2"
              value={row.impuesto || 'ITP'}
              disabled={readOnly}
              onChange={e => updateRow(idx, { impuesto: e.target.value })}
              data-testid={`${scenarioKey}-tax-impuesto-${idx}`}
            >
              {IMPUESTO_OPTIONS.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
            <select
              className="input-base text-[11px] py-0.5 col-span-7 truncate"
              value={row.base_fuente || 'precio_compra'}
              disabled={readOnly}
              onChange={e => updateRow(idx, { base_fuente: e.target.value })}
              data-testid={`${scenarioKey}-tax-base-${idx}`}
              title={baseLabels[row.base_fuente] || row.base_fuente}
            >
              {CONCRETE_BASE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <div className="col-span-2 relative">
              <input
                type="number"
                step="0.1"
                className="input-base text-[11px] py-0.5 text-right pr-4"
                value={row.tipo ?? 0}
                disabled={readOnly}
                onChange={e => updateRow(idx, { tipo: parseFloat(e.target.value) || 0 })}
                data-testid={`${scenarioKey}-tax-tipo-${idx}`}
              />
              <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 pointer-events-none">%</span>
            </div>
            {!readOnly && (
              <button
                onClick={() => removeRow(idx)}
                className="col-span-1 text-slate-400 hover:text-red-500 text-sm"
                title="Eliminar línea"
                data-testid={`${scenarioKey}-tax-remove-${idx}`}
              >
                ×
              </button>
            )}
          </div>
          <div className="flex items-center justify-between text-[10px] text-slate-500 tabular-nums px-0.5">
            <span>Base resuelta: <span className="text-slate-700">{cur(baseAt(idx, row))}</span></span>
            <span>= <span className="font-semibold text-slate-800">{cur(importeAt(idx, row))}</span></span>
          </div>
        </div>
      ))}
      {!readOnly && (
        <button
          onClick={addRow}
          className="text-[11px] text-blue-600 hover:text-blue-700 flex items-center gap-1"
          data-testid={`${scenarioKey}-tax-add`}
        >
          + Añadir impuesto
        </button>
      )}
    </div>
  );
};
